import React from 'react';
import { motion } from 'framer-motion';
import communityImage from '../assets/image4.jpg';
import { Sparkles, Heart, Shield, Users, Globe, Lock, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const gifBackground = "https://ik.imagekit.io/confesz/main22.avif";

function About() {
  const { t } = useTranslation();

  return (
    <div className="relative bg-black text-white overflow-x-hidden">
      {/* Hero Section with Video/Image */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src={gifBackground}
            alt="Background animation"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(40%)' }}
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 via-black/60 to-yellow-500/20" />
        </div>

        {/* Floating orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-400 rounded-full mix-blend-screen filter blur-3xl opacity-30 animate-blob" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-yellow-300 rounded-full mix-blend-screen filter blur-3xl opacity-30 animate-blob animation-delay-2000" />

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 bg-yellow-400/10 backdrop-blur-lg px-4 py-2 rounded-full mb-8 border border-yellow-400/20"
          >
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-medium text-yellow-200">{t('about.hero.badge')}</span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-6xl md:text-8xl font-bold mb-6"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-400">
              {t('about.hero.title')}
            </span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl text-gray-300 max-w-2xl mx-auto"
          >
            {t('about.hero.subtitle')}
          </motion.p>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-yellow-400/60 rounded-full flex justify-center">
            <div className="w-1 h-2 bg-yellow-400 rounded-full mt-2 animate-bounce" />
          </div>
        </motion.div>
      </section>

      {/* Mission Statement Section */}
      <section className="relative z-10 py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-400">
                {t('about.mission.title')}
              </span>
            </h2>
            <p className="text-2xl text-gray-300 leading-relaxed">
              {t('about.mission.statement1')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gray-900/50 backdrop-blur-lg border border-yellow-500/20 rounded-2xl p-8 mb-8"
          >
            <p className="text-xl text-gray-300 leading-relaxed">
              {t('about.mission.statement2')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="relative z-10 py-20 border-y border-yellow-500/10">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-center mb-16"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-400">
              {t('about.values.title')}
            </span>
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: t('about.values.privacy.title'),
                desc: t('about.values.privacy.desc')
              },
              {
                icon: Heart,
                title: t('about.values.freedom.title'),
                desc: t('about.values.freedom.desc')
              },
              {
                icon: Users,
                title: t('about.values.trust.title'),
                desc: t('about.values.trust.desc')
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-amber-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl blur-xl" />
                <div className="relative bg-gray-900/50 backdrop-blur-lg border border-yellow-500/10 rounded-2xl p-8 h-full hover:border-yellow-500/30 transition-colors">
                  <value.icon className="w-12 h-12 mb-4 text-yellow-400" />
                  <h3 className="text-xl font-bold mb-2 text-yellow-200">{value.title}</h3>
                  <p className="text-gray-400">{value.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="relative z-10 py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <p className="text-xl text-gray-300 leading-relaxed">
              {t('about.philosophy.paragraph1')}
            </p>
            
            <p className="text-xl text-gray-300 leading-relaxed">
              {t('about.philosophy.paragraph2')}
            </p>
            
            <div className="bg-gradient-to-r from-yellow-400/10 to-amber-400/10 border-l-4 border-yellow-400 p-6 rounded-r-2xl">
              <p className="text-2xl italic text-gray-200">
                {t('about.philosophy.quote')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bottom Image Section with CTA */}
      <section className="relative h-screen">
        <div className="absolute inset-0">
          <img
            src={communityImage}
            alt="Community"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(40%)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        </div>

        {/* Floating orbs */}
        <div className="absolute bottom-20 left-10 w-72 h-72 bg-yellow-400 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-400 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob animation-delay-2000" />

        {/* Content Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-300 to-amber-400">
                {t('about.cta.title')}
              </span>
            </h2>
            
            <p className="text-xl text-gray-300 mb-8">
              {t('about.cta.subtitle')}
            </p>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            
            <Link to="/downloads">
              <motion.button
                whileHover={{ scale: 1.05 }}
                className="clickable px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-400 rounded-full font-bold text-black text-lg hover:shadow-lg hover:shadow-yellow-400/40 transition-all"
              >
                {t('about.cta.button')}
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default About;